<template>
  <div class="media_item">
    <a
      v-if="mediaItem.attribution"
      :href="mediaItem.attribution.takedownUrl"
      class="remove_btn"
      target="_blank"
    >
      <i class="fas fa-flag-alt" />
    </a>
    <button
      v-else-if="canEditLocation"
      class="remove_btn"
      @click="deleteItem"
    >
      <svg
        fill="none"
        height="14"
        viewBox="0 0 15 14"
        width="15"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.4375 3.0625L3.5625 10.9375"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
        />
        <path
          d="M11.4375 10.9375L3.5625 3.0625"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
        />
      </svg>
    </button>
    <div class="thumbnail">
      <img
        :src="scaledGoogleUrl"
        referrerpolicy="no-referrer"
        @click="openMediaItemPreviewModal(mediaItem.google_url)"
      >
    </div>
    <div class="media_info">
      <span :class="labelTypes[labelCategory]">
        {{ labelCategory ? labelCategory.replace('_', ' ').toLowerCase() : '' }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Swal from 'sweetalert2';

export default {
  props: {
    mediaItem: {
      type: Object,
      required: true,
      visible: false,
    }
  },
  data() {
    return {
      labelTypes: {
        COVER: 'yellow_label',
        PROFILE: 'green_label',
        LOGO: 'blue_label',
        EXTERIOR: 'purple_label',
        INTERIOR: 'yellow_label',
        PRODUCT: 'green_label',
        AT_WORK: 'blue_label',
        FOOD_AND_DRINK: 'purple_label',
        MENU: 'yellow_label',
        COMMON_AREA: 'green_label',
        ROOMS: 'blue_label',
        TEAMS: 'purple_label',
        ADDITIONAL: 'yellow_label',
      }
    }
  },
  computed: {
    ...mapGetters('user', [
        'canEditLocation',
    ]),
    ...mapGetters('businessProfile', [
      'selectedLocation',
      'isInViewport',
    ]),
    labelCategory() {
      return this.mediaItem.category ?? null;
    },
    scaledGoogleUrl() {
      // Regex to remove any existing 0 scale from the url, then bolt on the correct one
      let url = this.mediaItem.google_url.replace(new RegExp("\\=s0$"), '');
      return url + '=s228-c';
    },
  },
  methods: {
    ...mapActions('businessProfile', [
      'deleteMediaItem',
      'openMediaItemPreviewModal',
    ]),
    deleteItem() {
      Swal.fire({
        title: 'Are your sure?',
        icon: 'warning',
        html: `You are about to delete this media item from both our platform and from your Google Business Profile.`,
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#d33',
        showLoaderOnConfirm: true,
        backdrop: true,
        preConfirm: async () => {
          const {selectedLocation, mediaItem} = this;
          await this.deleteMediaItem({id: selectedLocation, mediaItemId: mediaItem.id})
              .then(response => {
                return response;
              })
              .catch(error => {
                Swal.showValidationMessage(
                    `Request failed: ${error}`
                )
              });
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: 'Deleted successfully',
            icon: 'success'
          });
        }
      });
    },
    formatViews(viewCount) {
      if (viewCount === null) {
        return '-';
      }

      const views = parseInt(viewCount);
      if (views > 0) {
        return views.toLocaleString();
      }
      return views;
    }
  }
}
</script>

<style lang="scss" scoped>
.media_item {
  width: 230px;
  height: 230px;
  border: 1px solid #F7F5F2;
  border-radius: 0.75rem;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;

  > .remove_btn {
    background-color: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 7px;
    right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-transition: .3s;
    -o-transition: .3s;
    transition: .3s;
    color: #FFFFFF;
    font-size: .65rem;

    &:hover {
      background-color: var(--negative-color-dark);
    }
  }

  > .thumbnail {
    height: 75%;
    overflow: hidden;

    img {
      cursor: pointer;
    }
  }

  > .media_info {
    height: 25%;
    width: 100%;
    background-color: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;

    > span {
      padding: 0.25rem 0.5rem;
      border-radius: 6px;

      &.yellow_label {
        background-color: #FDF7DB;
        color: #EAB64D;
      }

      &.green_label {
        background-color: #DFF5EE;
        color: #0A8080;
      }

      &.blue_label {
        background-color: #E4FAFD;
        color: #1993AE;
      }

      &.purple_label {
        background-color: #F0E9FF;
        color: #5A3CD7;
      }
    }

    > .views {
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      color: var(--primary-font-color);

      > .icon {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        background-repeat: no-repeat;
        background-position: center;

        :deep(path) {
          stroke: var(--primary-font-color);
        }
      }

      > span {
        margin-left: 0.25rem;
        font-size: .8rem;
        font-weight: 500;
        line-height: 1.3;
        letter-spacing: -0.1px;
      }
    }
  }

}
</style>
